import { useEffect, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'react-fast-compare';
import moment from 'moment';
import { notifyError, getSelectedDateRange } from '../components/map/components/utils';
import {
  getAvailableSatelliteData, getSatelliteDataByDataTypeAndDate,
  getAvailableDroneData, getDroneImageryTilesData, getSatelliteCombinedDataByDataTypeAndDate
} from './satelliteImageryHelper';
import { droneDataType } from '../components/map/components/satelliteimagery/SatelliteImagery';

const dataTypesObj = {
  'Plant Vigor (NDVI)': 'NDVI',
  'NDVI': 'NDVI',
  'NDWI_SWIR': 'NDWI_SWIR',
  'NDRE': 'NDRE',
}

export const useRanchBlockPreference = (selectedRanchId, selectedBlockId, dispatchSatellite) => {
  useEffect(() => {
    // Set ranch/block preference to local reducer
    if (selectedBlockId?.payload?.length) {
      const [bId] = selectedBlockId.payload;
      dispatchSatellite({ type: 'setSelectedBlockId', payload: bId });
    } else {
      dispatchSatellite({ type: 'setShowMap3D', payload: false });
      dispatchSatellite({ type: 'setSelectedBlockId', payload: undefined });
    }
    if (selectedRanchId?.payload?.length) {
      const [rId] = selectedRanchId.payload;
      dispatchSatellite({ type: 'setSelectedRanchId', payload: rId });
    } else {
      dispatchSatellite({ type: 'setSelectedRanchId', payload: undefined });
    }
  }, [selectedRanchId, selectedBlockId, dispatchSatellite]);
};

export const useSatelliteImagery = (selectedRanchId,
  selectedBlockId, latestReload, callApi, dispatchSatellite, t) => {
  useEffect(() => {
    if (!callApi) return;

    // get satellite imagery using local reducer
    let type;
    let id;

    if (selectedBlockId) {
      type = 'block';
      id = selectedBlockId;
    } else if (selectedRanchId) {
      type = 'ranch';
      id = selectedRanchId;
    }

    if (type && id) {
      (async () => {
        try {
          const data = await getAvailableSatelliteData(type, id);
          const dataObj = {};
          const dataArr = [];
          const uniqueDataTypes = {};
          data.forEach((d) => {
            const dateString = moment(d.date * 1000).format('YYYY-MM-DD');
            const momentDate = moment(dateString, 'YYYY-MM-DD');
            if (!dataObj[dateString]) {
              dataObj[dateString] = { dateString, momentDate };
              d.datatypes.forEach((dt) => {
                dataObj[dateString][dt] = d.date;
                uniqueDataTypes[dt] = dt;
              });
            } else {
              d.datatypes.forEach((dt) => {
                uniqueDataTypes[dt] = dt;
                if (!dataObj[dateString][dt]) {
                  dataObj[dateString][dt] = d.date;
                } else if (Number(dataObj[dateString][dt]) < Number(d.date)) {
                  dataObj[dateString][dt] = d.date;
                }
              });
            }
          });

          Object.keys(dataObj).forEach((k) => {
            dataArr.push(dataObj[k]);
          });

          const availableSatelliteTypes = Object.keys(uniqueDataTypes)
            .filter((dt) => dt.startsWith('satellite:'))
            .map((k) => k.split(':')[1]);

          const availableAerialTypes = Object.keys(uniqueDataTypes)
            .filter((dt) => dt.startsWith('aerial:'))
            .map((k) => k.split(':')[1]);

          const availableDataSources = [];
          if (availableSatelliteTypes.length) {
            availableDataSources.push('satellite');
          }

          if (availableAerialTypes.length) {
            availableDataSources.push('aerial');
          }

          dispatchSatellite({
            type: 'setSatelliteDates',
            payload: {
              type,
              id: type === 'ranch'
                ? selectedRanchId
                : selectedBlockId,
              datesObj: dataObj,
              datesArray: dataArr,
              uniqueDataTypes,
              availableSatelliteTypes,
              availableAerialTypes,
              availableDataSources,
            },
          });
        } catch (error) {
          notifyError(t(error.message));
        }
      })().catch();
    }
  }, [selectedRanchId, selectedBlockId, latestReload, dispatchSatellite, callApi]);
};

export const useNewSatelliteImagery = (
  selectedRanchId,
  selectedBlockId,
  selectedSatelliteDataType,
  selectedSatelliteDateRange,
  satelliteDates,
  selectedSatelliteDate,
  latestReload,
  callApi,
  dispatchSatellite,
  dispatchSatelliteLeft,
  dispatchSatelliteRight,
  isLeft,
  t
) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  const dateKeysStr = JSON.stringify(Object.keys(satelliteDates?.datesObj || {})
    ?.sort((a, b) => new Date(a) - new Date(b)));
  const { startDate, endDate } = selectedSatelliteDateRange || {};
  const sDate = startDate || moment().startOf('day').subtract(2, 'months').toISOString();
  const eDate = endDate || moment().endOf('day').toISOString();
  const dataType = dataTypesObj?.[selectedSatelliteDataType];
  const selectedDate = moment(selectedSatelliteDate).format('YYYY-MM-DD');
  const [isDataExists, setIsDataExists] = useState(false);
  const [isCallNeeded, setIsCallNeeded] = useState(true);
  const [stDate, setStDate] = useState(sDate);
  const [enDate, setEnDate] = useState(eDate);
  const [localRanch, setLocalRanch] = useState(undefined);
  const [localBlock, setLocalBlock] = useState(undefined);

  const retryRef = useRef(0);
  const isFetchingRef = useRef(false);
  const MAX_RETRIES = 5;

  const { dataType: exDataType } =
    satelliteDates?.datesArray?.[0] || {};
  const isDataTypeSame = dataType === exDataType;

  // To handle the API call with dates
  useEffect(() => {
    const dateKeys = JSON.parse(dateKeysStr || '[]');
    if (!dateKeys.length) return;

    const exists = dateKeys.includes(selectedDate);
    setIsDataExists(exists);

    if (!exists) return;

    const isFirstDate = dateKeys.indexOf(selectedDate) === 0;

    if (!isDataTypeSame) {
      setIsDataExists(false);
      setIsCallNeeded(true);
    }

    if (isFirstDate) {
      setIsDataExists(false);
      setIsCallNeeded(true);
      setStDate(
        moment(dateKeys[0])
          .startOf('day')
          .subtract(1, 'months')
          .toISOString()
      );
      setEnDate(
        moment(dateKeys[dateKeys.length - 1])
          .endOf('day')
          .toISOString()
      );
    }

    if (selectedBlockId && !isEqual(selectedBlockId, localBlock)) {
      setLocalBlock(selectedBlockId);
      setLocalRanch(undefined);
      setIsCallNeeded(true);
      setIsDataExists(false);
    }

    if (selectedRanchId && !isEqual(selectedRanchId, localRanch)) {
      setLocalRanch(selectedRanchId);
      setLocalBlock(undefined);
      setIsCallNeeded(true);
      setIsDataExists(false);
    }

    if (!isLeft && isEqual(selectedBlockId, localBlock)) {
      setIsCallNeeded(true);
      setIsDataExists(false);
    }

    if (!isLeft && isEqual(selectedRanchId, localRanch)) {
      setIsCallNeeded(true);
      setIsDataExists(false);
    }
  }, [
    dateKeysStr,
    isDataTypeSame,
    selectedDate,
    isLeft,
    selectedBlockId,
    selectedRanchId,
  ]);

  // Reset retries when ranch block changes
  useEffect(() => {
    retryRef.current = 0;
    isFetchingRef.current = false;
  }, [selectedRanchId, selectedBlockId, selectedSatelliteDataType]);

  // API calls with retry logic
  useEffect(() => {
    if (!callApi) return;
    if (isFetchingRef.current) return;

    let type;
    let id;

    if (selectedBlockId) {
      type = 'block';
      id = selectedBlockId;
    } else if (selectedRanchId) {
      type = 'ranch';
      id = selectedRanchId;
    }

    if (!type || !id || isDataExists || !isCallNeeded) return;
    const fetchData = async () => {
      isFetchingRef.current = true;

      try {
        const { data = [] } =
          (await getSatelliteCombinedDataByDataTypeAndDate(
            type,
            id,
            dataType,
            stDate,
            enDate
          )) || {};

        // Retry when data is empty or 1
        if (data.length <= 1 && retryRef.current < MAX_RETRIES) {
          retryRef.current += 1;

          const newEnd = moment(stDate)
            .subtract(1, 'day')
            .endOf('day')
            .toISOString();

          const newStart = moment(stDate)
            .subtract(2, 'months')
            .startOf('day')
            .toISOString();

          setStDate(newStart);
          setEnDate(newEnd);

          isFetchingRef.current = false;
          return;
        }

        // To stop calls
        setIsCallNeeded(false);
        retryRef.current = 0;

        if (!data.length) {
          isFetchingRef.current = false;
          return;
        }

        const dataObj = {};
        const dataArr = [];

        data.forEach(d => {
          const dateString = moment(d.date * 1000).format('YYYY-MM-DD');
          const momentDate = moment(dateString, 'YYYY-MM-DD');

          if (!dataObj[dateString]) {
            dataObj[dateString] = {
              type,
              id,
              dateString,
              momentDate,
              data: [d],
              dataSource: 'satellite',
              dataType,
            };
          } else {
            dataObj[dateString].data.push(d);
          }
        });

        Object.values(dataObj).forEach((k) => dataArr.push(k));

        dispatchSatellite({
          type: 'setSatelliteDates',
          payload: {
            type,
            id,
            datesObj: dataObj,
            datesArray: dataArr,
          },
        });
        if (dispatchSatelliteArg) {
          dispatchSatelliteArg({
            type: 'setSatelliteDates',
            payload: {
              type,
              id,
              datesObj: dataObj,
              datesArray: dataArr,
            },
          });
        }
      } catch (error) {
        notifyError(t(error.message));
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [
    callApi,
    selectedRanchId,
    selectedBlockId,
    dataType,
    stDate,
    enDate,
    isDataExists,
    isCallNeeded,
    latestReload,
    dispatchSatellite,
    dispatchSatelliteArg,
  ]);
};

export const useSatelliteDataDatesAndTypes = (satelliteDates,
  selectedSatelliteDataType, selectedSatelliteDataSource, dispatchSatellite, t) => {
  const dataSourceRef = useRef(undefined);
  useEffect(() => {
    // prepare satellite data dates and types using local reducer
    dataSourceRef.current = selectedSatelliteDataSource;
    dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });
    if (satelliteDates) { // this is initial or there are no dates
      const selectedSatelliteDatesFilteredSkeleton = {
        id: satelliteDates.id,
        type: satelliteDates.type,
        datesObj: {},
        datesArray: [],
      };

      if (!satelliteDates.availableDataSources.length) {
        notifyError(t('No data sources for satellite imagery available'));
        dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });
        return;
      }


      if (
        selectedSatelliteDataSource === 'satellite'
          && !satelliteDates.availableSatelliteTypes
            .includes(selectedSatelliteDataType)
      ) {
        if (!satelliteDates.availableSatelliteTypes.length) {
          notifyError(t('No available satellite imagery types'));
          dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });
          return;
        }
        dispatchSatellite({
          type: 'setSelectedSatelliteDataType',
          payload: 'NDVI', // To set the default option - SWB-1345
        });
        return;
      }

      if (
        selectedSatelliteDataSource === 'aerial'
          && !satelliteDates.availableAerialTypes
            .includes(selectedSatelliteDataType)
      ) {
        if (!satelliteDates.availableAerialTypes.length) {
          notifyError(t('No available satellite imagery types'));
          dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });
          return;
        }
        dispatchSatellite({
          type: 'setSelectedSatelliteDataType',
          payload: 'NDVI', // To set the default option - SWB-1345
        });
        return;
      }

      const selectedSatelliteDatesFiltered = satelliteDates.datesArray
        .reduce((acc, curr) => {
          const dataSourceAndType = `${selectedSatelliteDataSource}:${selectedSatelliteDataType}`;
          if (!curr[dataSourceAndType]) {
            return acc;
          }

          const dateObj = {};
          dateObj[dataSourceAndType] = curr[dataSourceAndType];
          dateObj.time = curr[dataSourceAndType];
          dateObj.dateString = curr.dateString;
          dateObj.momentDate = curr.momentDate;

          dateObj.dataSource = selectedSatelliteDataSource;
          dateObj.dataType = selectedSatelliteDataType;

          dateObj.id = satelliteDates.id;
          dateObj.type = satelliteDates.type;

          acc.datesArray.push(cloneDeep(dateObj));
          acc.datesObj[curr.dateString] = cloneDeep(dateObj);
          return acc;
        }, selectedSatelliteDatesFilteredSkeleton);

      if (!selectedSatelliteDatesFiltered.datesArray.length) {
        dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });

        return;
      }

      dispatchSatellite({
        type: 'setSelectedSatelliteDatesFiltered',
        payload: selectedSatelliteDatesFiltered,
      });
    }
  }, [satelliteDates, selectedSatelliteDataType, selectedSatelliteDataSource, dispatchSatellite]);
};

export const useNewSatelliteDataDatesAndTypes = (satelliteDates,
  selectedSatelliteDataType, selectedSatelliteDataSource, dispatchSatellite, t) => {
  const dataSourceRef = useRef(undefined);
  useEffect(() => {
    // prepare satellite data dates and types using local reducer
    dataSourceRef.current = selectedSatelliteDataSource;
    dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });
    if (satelliteDates) { // this is initial or there are no dates
      const selectedSatelliteDatesFilteredSkeleton = {
        id: satelliteDates.id,
        type: satelliteDates.type,
        datesObj: {},
        datesArray: [],
      };

      const selectedSatelliteDatesFiltered = satelliteDates.datesArray
        .reduce((acc, curr) => {
          const dateObj = {};
          dateObj.dateString = curr.dateString;
          dateObj.momentDate = curr.momentDate;

          dateObj.dataSource = selectedSatelliteDataSource;
          dateObj.dataType = dataTypesObj?.[selectedSatelliteDataType];

          dateObj.id = satelliteDates.id;
          dateObj.type = satelliteDates.type;

          acc.datesArray.push(cloneDeep(dateObj));
          acc.datesObj[curr.dateString] = cloneDeep(dateObj);
          return acc;
        }, selectedSatelliteDatesFilteredSkeleton);

      if (!selectedSatelliteDatesFiltered.datesArray.length) {
        dispatchSatellite({ type: 'setSelectedSatelliteDatesFiltered', payload: undefined });

        return;
      }

      dispatchSatellite({
        type: 'setSelectedSatelliteDatesFiltered',
        payload: selectedSatelliteDatesFiltered,
      });
    }
  }, [satelliteDates, selectedSatelliteDataType, selectedSatelliteDataSource, dispatchSatellite]);
};

export const useSatelliteDateRangePreparation = (selectedSatelliteDatesFiltered,
  selectedSatelliteDate, selectedSatelliteDatesDataRange, selectedSatelliteDataType,
  selectedSatelliteDataSource, ranchId, blockId, dispatchSatellite, dispatchSatelliteLeft,
  dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  const dateRange = selectedSatelliteDatesDataRange
    ? selectedSatelliteDatesDataRange.map((d) => d.dateString) : [];
  const dataSource = selectedSatelliteDatesDataRange
    ? selectedSatelliteDatesDataRange.map((d) => d.dataSource) : [];
  const dataType = selectedSatelliteDatesDataRange
    ? selectedSatelliteDatesDataRange.map((d) => d.dataType) : [];

  const updateDateRangeWithData = ((!dateRange.includes(moment(selectedSatelliteDate)
    .format('YYYY-MM-DD')) || !dataSource.includes(selectedSatelliteDataSource)
    || !dataType.includes(selectedSatelliteDataType))
    || ((selectedSatelliteDatesDataRange && selectedSatelliteDatesDataRange[0].id !== blockId)
    && (selectedSatelliteDatesDataRange && selectedSatelliteDatesDataRange[0].id !== ranchId)));

  useEffect(() => {
    if (!selectedSatelliteDatesFiltered) {
      return;
    }

    if (updateDateRangeWithData) {
      const idxMax = selectedSatelliteDatesFiltered.datesArray.length - 1;
      if (!selectedSatelliteDate) {
        const selectedSatelliteDateLocal = selectedSatelliteDatesFiltered
          .datesArray[idxMax].momentDate;
        dispatchSatellite({
          type: 'setSelectedSatelliteDate',
          payload: selectedSatelliteDateLocal,
        });
        if (dispatchSatelliteArg) {
          dispatchSatelliteArg({
            type: 'setSelectedSatelliteDate',
            payload: selectedSatelliteDateLocal,
          });
        }
        return;
      }

      let idxSelected = -1;

      for (let i = idxMax; i > -1; i -= 1) {
        const selectedDateString = selectedSatelliteDate.format('YYYY-MM-DD');
        const currDateString = selectedSatelliteDatesFiltered.datesArray[i].dateString;
        if (selectedDateString === currDateString) {
          idxSelected = i;

          break;
        }
      }

      if (idxSelected < 0) {
        const selectedSatelliteDateLocal = selectedSatelliteDatesFiltered
          .datesArray[idxMax].momentDate;
        dispatchSatellite({
          type: 'setSelectedSatelliteDate',
          payload: selectedSatelliteDateLocal,
        });
        if (dispatchSatelliteArg) {
          dispatchSatelliteArg({
            type: 'setSelectedSatelliteDate',
            payload: selectedSatelliteDateLocal,
          });
        }
        return;
      }

      const selectedSatelliteDatesDataRangeLocal = getSelectedDateRange(
        selectedSatelliteDatesFiltered.datesArray,
        idxSelected,
      );

      dispatchSatellite({
        type: 'setSelectedSatelliteDatesDataRange',
        payload: selectedSatelliteDatesDataRangeLocal,
      });
      if (dispatchSatelliteArg) {
        dispatchSatelliteArg({
          type: 'setSelectedSatelliteDatesDataRange',
          payload: selectedSatelliteDatesDataRangeLocal,
        });
      }
    }
  }, [updateDateRangeWithData, dispatchSatellite, selectedSatelliteDate,
    selectedSatelliteDatesFiltered, dispatchSatelliteArg]);
};

export const useDefaultSelectedImageryData = (dateRangeWithDisplayData, selectedImageryData,
  selectedSatelliteDate, dispatchSatellite, dispatchSatelliteLeft, dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  useEffect(() => {
    const displayData = dateRangeWithDisplayData;
    const dateString = moment(selectedSatelliteDate).format('YYYY-MM-DD');

    const imageryData = selectedImageryData;
    if (displayData && displayData.length) {
      const filteredData = displayData.filter((d) => d && d.dateString === dateString);
      if (filteredData.length && !isEqual(filteredData[0].data, imageryData.data)
             && imageryData.visible) {
        dispatchSatellite({
          type: 'setSelectedImageryData',
          payload: {
            visible: true,
            data: filteredData[0].data,
          },
        });
        if (dispatchSatelliteArg) {
          dispatchSatelliteArg({
            type: 'setSelectedImageryData',
            payload: {
              visible: true,
              data: filteredData[0].data,
            },
          });
        }
      }
    }
  }, [dateRangeWithDisplayData, dispatchSatellite, selectedImageryData,
    selectedSatelliteDate, dispatchSatelliteArg]);
};

// Time conversion logic to render imagery data
function unixOffset(date) {
  let result;
  if (date) {
    // 1000: Number to add milliseconds to Epoch by multiplying Epoch with 1000
    const momentDate = moment(Number(date) * 1000);
    result = momentDate.unix() + (momentDate.utcOffset() * 60);
  }

  return result;
}

export const useUpdateDateRangeWithDisplayData = (satelliteDates, selectedSatelliteDatesDataRange,
  callApi, isSatellite, dispatchSatellite, dispatchSatelliteLeft, dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;

  useEffect(() => {
    if (!callApi || !isSatellite || !satelliteDates || !selectedSatelliteDatesDataRange) return;

    const dateRangeWithDisplayData = selectedSatelliteDatesDataRange.map((item) => {
      return { ...item, ...satelliteDates?.datesObj?.[item?.dateString] }
    })

      dispatchSatellite({
        type: 'setDateRangeWithDisplayData',
        payload: dateRangeWithDisplayData,
      });
      if (dispatchSatelliteArg) {
        dispatchSatelliteArg({
          type: 'setDateRangeWithDisplayData',
          payload: dateRangeWithDisplayData,
        });
      }
  }, [callApi, dispatchSatellite, selectedSatelliteDatesDataRange,
    isSatellite, dispatchSatelliteArg, satelliteDates])
}

export const useRequestSatelliteData = (selectedSatelliteDatesDataRange,
  callApi, isSatellite, dispatchSatellite, dispatchSatelliteLeft, dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  useEffect(() => {
    if (!callApi || !isSatellite) return;

    // request satellite data
    if (!selectedSatelliteDatesDataRange) {
      dispatchSatellite({ type: 'setDateRangeWithDisplayData', payload: undefined });

      return;
    }

    if (!selectedSatelliteDatesDataRange.length) {
      dispatchSatellite({ type: 'setDateRangeWithDisplayData', payload: undefined });

      return;
    }

    const dateRangeWithDisplayData = cloneDeep(selectedSatelliteDatesDataRange);
    const satelliteRequests = [];
    const satelliteRequestDates = [];
    selectedSatelliteDatesDataRange.forEach((dr) => {
      const {
        id,
        type,
        dataType,
        time,
      } = dr;
      satelliteRequestDates.push(unixOffset(time));
      satelliteRequests.push(getSatelliteDataByDataTypeAndDate(type, id, dataType, time));
    });

    Promise.allSettled(satelliteRequests)
      .then((results) => {
        results.forEach((r, index) => {
          if (r.status === 'fulfilled') {
            if (r?.value?.data?.length) {
              const dateString = moment(Number(satelliteRequestDates[index]) * 1000)
                .format('YYYY-MM-DD');
              for (let i = 0; i < dateRangeWithDisplayData.length; i += 1) {
                if (dateRangeWithDisplayData[i].dateString === dateString) {
                  dateRangeWithDisplayData[i].data = r.value.data;

                  break;
                }
              }
            }
          }
        });

        dispatchSatellite({
          type: 'setDateRangeWithDisplayData',
          payload: dateRangeWithDisplayData,
        });
        if (dispatchSatelliteArg) {
          dispatchSatelliteArg({
            type: 'setDateRangeWithDisplayData',
            payload: dateRangeWithDisplayData,
          });
        }
      });
  }, [callApi, dispatchSatellite, selectedSatelliteDatesDataRange,
    isSatellite, dispatchSatelliteArg]);
};

export const useRequestDroneDate = (ranchId, isSatellite, selectedDroneDate, blockIdStr,
  dispatchSatellite, isRecommendation, dispatchSatelliteLeft, dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  useEffect(() => {
    if (isSatellite || selectedDroneDate) return;

    // To show spinner when drone imagery is loading
    dispatchSatellite({
      type: 'setIsLoading',
      payload: true,
    });
    const data = [getAvailableDroneData(ranchId)];
    Promise.allSettled(data).then((results) => {
      results.forEach((r) => {
        const availableDroneData = r?.value?.data;
        if (!availableDroneData) return;
        if (!isRecommendation) {
          dispatchSatellite({
            type: 'setAvailableDroneData',
            payload: availableDroneData,
          });
          if (dispatchSatelliteArg) {
            dispatchSatelliteArg({
              type: 'setAvailableDroneData',
              payload: availableDroneData,
            });
          }
        }
        const availableDates = availableDroneData?.map((d) => {
          const obj = {
            ...d,
            momentDate: moment(d?.imageryCaptureDateTime),
            dateString: d?.imageryCaptureDateTime,
            dataSource: 'drone',
          };
          return obj;
        });

        const availableDateObj = availableDates.reduce((a, b) => {
          const obj = { ...a };
          const dateStr = moment(b.dateString).format('YYYY-MM-DD');
          obj[dateStr] = b;
          return obj;
        }, {});

        const availableDroneDates = { datesObj: availableDateObj, datesArray: availableDates, type: 'drone' };

        let dateKey;
        if (blockIdStr) {
          const filteredDates = availableDates.filter((d) => d?.blockId === blockIdStr);
          if (filteredDates?.length) {
            dateKey = moment(filteredDates[0]?.momentDate).format('YYYY-MM-DD');
          }
        }
        const selectedDroneDateLocal = availableDroneDates.datesObj[dateKey]?.momentDate;

        if (dateKey) {
          dispatchSatellite({
            type: 'setAvailableDroneDates',
            payload: availableDroneDates,
          });

          // To hide spinner when drone imagery is loaded
          dispatchSatellite({
            type: 'setIsLoading',
            payload: false,
          });

          if (!isRecommendation) {
            dispatchSatellite({
              type: 'setAvailableDroneDataTypes',
              payload: Object.keys(droneDataType),
            });
            if (dispatchSatelliteArg) {
              dispatchSatelliteArg({
                type: 'setAvailableDroneDataTypes',
                payload: Object.keys(droneDataType),
              });
            }
          }

          dispatchSatellite({
            type: 'setSelectedDroneDate',
            payload: selectedDroneDateLocal,
          });

          dispatchSatellite({
            type: 'setSelectedDroneData',
            payload: { visible: true, data: availableDroneDates.datesObj[dateKey] },
          });
          if (dispatchSatelliteArg) {
            dispatchSatelliteArg({
              type: 'setAvailableDroneDates',
              payload: availableDroneDates,
            });

            dispatchSatelliteArg({
              type: 'setSelectedDroneDate',
              payload: selectedDroneDateLocal,
            });

            dispatchSatelliteArg({
              type: 'setSelectedDroneData',
              payload: { visible: true, data: availableDroneDates.datesObj[dateKey] },
            });
          }
        }
      });
    }).then(() => {
      // To hide spinner when drone imagery is not available
      dispatchSatellite({
        type: 'setIsLoading',
        payload: false,
      });
    }).catch(() => {
      // To hide spinner if any error
      dispatchSatellite({
        type: 'setIsLoading',
        payload: false,
      });
    });
  }, [blockIdStr, dispatchSatellite, isRecommendation, isSatellite,
    ranchId, selectedDroneDate, dispatchSatelliteArg]);
};

export const useDroneImageryDataForSelectedDate = (ranchId, selectedDroneDataType,
  selectedDroneData, dispatchSatellite, dispatchSatelliteLeft, dispatchSatelliteRight) => {
  const dispatchSatelliteArg = dispatchSatelliteLeft || dispatchSatelliteRight;
  const { imageryTaskId, imageryProjectId, imageryAssets } = selectedDroneData?.data || {};

  useEffect(() => {
    if (!selectedDroneData?.data) return;
    if (!imageryTaskId || !imageryProjectId) return;

    const dsm = selectedDroneDataType === 'NDSM' ? 'dsm' : null;
    const defaultAssetType = 'orthophoto';
    const availableAssetTypes = imageryAssets.map((d) => d?.imageryType);

    // To get the asset type based on the drone imagery type selection
    let assetType;
    if (availableAssetTypes?.includes('dsm') && availableAssetTypes?.includes('chm')
       && selectedDroneDataType === 'NDSM') {
      assetType = 'chm';
    } else {
      const [filteredAssetType] = imageryAssets.filter((d) => (d?.downloadURL?.indexOf(`/${dsm}.tif`) !== -1
      || d?.downloadURL?.indexOf(`/${selectedDroneDataType?.toLowerCase()}.tif`) !== -1))
        ?.map((d) => d?.imageryType);
      assetType = filteredAssetType;
    }

    // To show spinner when drone imagery is loading
    dispatchSatellite({
      type: 'setIsLoading',
      payload: true,
    });
    getDroneImageryTilesData(assetType || defaultAssetType, ranchId,
      imageryTaskId, imageryProjectId).then((result) => {
      dispatchSatellite({
        type: 'setSelectedDroneTilesData',
        payload: result?.data,
      });
      dispatchSatellite({
        type: 'setSelectedDroneImagery',
        payload: { visible: true, data: [result?.data] },
      });

      // To hide spinner when drone imagery is loaded
      dispatchSatellite({
        type: 'setIsLoading',
        payload: false,
      });
      if (dispatchSatelliteArg) {
        dispatchSatelliteArg({
          type: 'setSelectedDroneTilesData',
          payload: result?.data,
        });
        dispatchSatelliteArg({
          type: 'setSelectedDroneImagery',
          payload: { visible: true, data: [result?.data] },
        });
      }
    });
  }, [dispatchSatellite, imageryProjectId, imageryTaskId, ranchId,
    selectedDroneData, selectedDroneDataType, imageryAssets, dispatchSatelliteArg]);
};
